import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ScanText } from "lucide-react";
import { wmsApi, fmtKg2, fmtDate } from "./wmsApi";
import { WButton, WInput, WField, WCard, WBadge, WModal } from "./WmsUi";
import WmsScanDocument from "./WmsScanDocument";

export default function WmsReceipts() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [receipts, setReceipts] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(params.get("new") === "1");
  const [scanOpen, setScanOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_name: "", ddt_number: "", ddt_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    wmsApi.get("/wms/receipts", { params: { q: q || undefined } }).then((r) => setReceipts(r.data)).catch(() => {});
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { wmsApi.get("/wms/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, [showNew]);

  const create = async () => {
    if (!form.supplier_name.trim()) { toast.error("Inserisci il fornitore"); return; }
    setSaving(true);
    try {
      const r = await wmsApi.post("/wms/receipts", form);
      toast.success(`Carico ${r.data.number} creato`);
      navigate(`/wms/carichi/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione carico");
    } finally { setSaving(false); }
  };

  const supMatches = form.supplier_name.length >= 1
    ? suppliers.filter((s) => s.name.toLowerCase().includes(form.supplier_name.toLowerCase()) && s.name.toLowerCase() !== form.supplier_name.toLowerCase()).slice(0, 5)
    : [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Chivo'] font-black text-3xl tracking-tighter uppercase">Carichi</h1>
        <div className="flex gap-2 flex-wrap">
          <WButton variant="dark" onClick={() => setScanOpen(true)} data-testid="scan-doc-btn"><ScanText className="w-5 h-5" /> Scansiona bolla (AI)</WButton>
          <WButton variant="primary" onClick={() => setShowNew(true)} data-testid="new-receipt-btn"><Plus className="w-5 h-5" /> Nuovo carico</WButton>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <WInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca numero, fornitore, DDT..." className="pl-12" data-testid="receipts-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {receipts.map((r) => (
          <WCard key={r.id} className="p-4 cursor-pointer hover:-translate-y-1 hover:shadow-[5px_5px_0px_#09090b] transition-transform border-2 border-zinc-300"
            onClick={() => navigate(`/wms/carichi/${r.id}`)} data-testid={`receipt-card-${r.number}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-['Chivo'] font-black text-lg">{r.number}</p>
              <div className="flex gap-1">
                {r.esaurito && <WBadge tone="green">Tutto scaricato</WBadge>}
                <WBadge tone={r.status === "aperto" ? "orange" : "green"}>{r.status}</WBadge>
              </div>
            </div>
            <p className="font-bold text-zinc-800 mt-1 truncate">{r.supplier_name}</p>
            <p className="text-sm text-zinc-500">DDT {r.ddt_number || "—"} · {fmtDate(r.ddt_date)}</p>
            <div className="flex gap-4 mt-3 text-sm font-semibold text-zinc-700 flex-wrap">
              <span>{r.lots_count} articoli</span>
              <span>{r.pieces_count} pezzi</span>
              <span>{fmtKg2(r.kg_total)} kg</span>
              <span className={r.kg_residuo > 0 ? "text-[#E03C00]" : "text-green-700"}>residuo {fmtKg2(r.kg_residuo)} kg</span>
            </div>
          </WCard>
        ))}
        {receipts.length === 0 && <p className="text-zinc-500 py-8" data-testid="receipts-empty">Nessun carico. Crea il primo con "Nuovo carico".</p>}
      </div>

      <WmsScanDocument open={scanOpen} onClose={() => { setScanOpen(false); load(); }} />

      <WModal open={showNew} onClose={() => setShowNew(false)} title="Nuovo carico — Ricevimento merce">
        <div className="space-y-4">
          <WField label="Fornitore">
            <WInput value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Nome fornitore" data-testid="receipt-supplier-input" autoFocus />
            {supMatches.length > 0 && (
              <div className="border-2 border-zinc-300 border-t-0 bg-white">
                {supMatches.map((s) => (
                  <button key={s.id} className="w-full text-left px-4 h-12 hover:bg-orange-50 font-semibold" onClick={() => setForm({ ...form, supplier_name: s.name })} data-testid={`supplier-suggest-${s.id}`}>
                    {s.name} <span className="text-xs text-zinc-400">({s.receipts_count || 0} carichi)</span>
                  </button>
                ))}
              </div>
            )}
          </WField>
          <div className="grid grid-cols-2 gap-3">
            <WField label="Numero DDT fornitore">
              <WInput value={form.ddt_number} onChange={(e) => setForm({ ...form, ddt_number: e.target.value })} placeholder="es. 4521" data-testid="receipt-ddt-input" />
            </WField>
            <WField label="Data DDT">
              <WInput type="date" value={form.ddt_date} onChange={(e) => setForm({ ...form, ddt_date: e.target.value })} data-testid="receipt-date-input" />
            </WField>
          </div>
          <WButton variant="primary" className="w-full" onClick={create} disabled={saving} data-testid="receipt-create-btn">
            {saving ? "Creazione..." : "Crea carico e aggiungi articoli"}
          </WButton>
        </div>
      </WModal>
    </div>
  );
}
